import React from 'react';
import { types as sdkTypes } from '../../util/sdkLoader';
import { userLocation } from '../../util/maps';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

const GENERATED_BOUNDS_DEFAULT_DISTANCE = 500; // meters

// Nominatim usage policy: max 1 request per second.
// We keep one pending throttled call at a time. When a new search query
// arrives while we are still waiting for the throttle window to open,
// we cancel the older pending call and replace it with the latest URL
// so that only the most relevant request actually hits the network.
const THROTTLE_MS = 1000;
let lastRequestTime = 0;
let pendingThrottleTimer = null;
let pendingReject = null;

const throttledFetch = url => {
  // Cancel any earlier still-waiting fetch — the new query supersedes it.
  if (pendingThrottleTimer) {
    window.clearTimeout(pendingThrottleTimer);
    pendingThrottleTimer = null;
    if (pendingReject) {
      const reject = pendingReject;
      pendingReject = null;
      reject({ __cancelled: true });
    }
  }

  const now = Date.now();
  const wait = Math.max(0, THROTTLE_MS - (now - lastRequestTime));

  return new Promise((resolve, reject) => {
    pendingReject = reject;
    pendingThrottleTimer = window.setTimeout(() => {
      pendingThrottleTimer = null;
      pendingReject = null;
      lastRequestTime = Date.now();
      fetch(url, {
        headers: {
          'Accept-Language': 'fr',
          'User-Agent': 'CobucoV2-Marketplace/1.0',
        },
      }).then(resolve, reject);
    }, wait);
  });
};

// Approximate bounding box from a point and distance in meters
const locationBounds = (latlng, distance) => {
  if (!latlng) return null;
  const latDelta = distance / 111320;
  const lngDelta = distance / (111320 * Math.cos((latlng.lat * Math.PI) / 180));
  return new SDKLatLngBounds(
    new SDKLatLng(latlng.lat + latDelta, latlng.lng + lngDelta),
    new SDKLatLng(latlng.lat - latDelta, latlng.lng - lngDelta)
  );
};

const placeOrigin = prediction => {
  if (prediction && prediction.lat && prediction.lon) {
    return new SDKLatLng(parseFloat(prediction.lat), parseFloat(prediction.lon));
  }
  return null;
};

const placeBounds = prediction => {
  if (prediction && prediction.boundingbox && prediction.boundingbox.length === 4) {
    // Nominatim boundingbox: [south, north, west, east]
    const [south, north, west, east] = prediction.boundingbox.map(parseFloat);
    return new SDKLatLngBounds(new SDKLatLng(north, east), new SDKLatLng(south, west));
  }
  // Fallback: generate bounds around origin
  const origin = placeOrigin(prediction);
  return origin ? locationBounds(origin, GENERATED_BOUNDS_DEFAULT_DISTANCE) : null;
};

const normalizeNominatimComponents = prediction => {
  const addr = prediction.address || {};
  return {
    route: addr.road || null,
    streetNumber: addr.house_number || null,
    locality: addr.city || addr.town || addr.village || addr.municipality || null,
    sublocality: addr.suburb || addr.neighbourhood || null,
    postal: addr.postcode || null,
    country: addr.country || null,
  };
};

export const GeocoderAttribution = ({ className }) => (
  <span
    className={className}
    style={{
      fontSize: '11px',
      padding: '6px 16px',
      display: 'block',
      color: '#8e8e93',
      borderTop: '1px solid #f2f2f7',
      textAlign: 'right',
    }}
  >
    © OpenStreetMap contributors
  </span>
);

class GeocoderNominatim {
  getPlacePredictions(search, countryLimit, locale) {
    const countryCodes = Array.isArray(countryLimit)
      ? countryLimit.join(',')
      : countryLimit || 'FR';

    const params = new URLSearchParams({
      q: search,
      format: 'json',
      addressdetails: '1',
      limit: '5',
      countrycodes: countryCodes,
    });

    const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;

    return throttledFetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Nominatim API error: ${response.status}`);
        }
        return response.json();
      })
      .then(results => ({
        search,
        predictions: results,
      }))
      .catch(err => {
        // A cancelled throttled call means a newer query took over.
        // Return an empty result tagged with a search value that will
        // never match the current one so the caller silently drops it.
        if (err && err.__cancelled) {
          return { search: null, predictions: [] };
        }
        throw err;
      });
  }

  getPredictionId(prediction) {
    return prediction.place_id ? String(prediction.place_id) : prediction.id;
  }

  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      return prediction.predictionPlace.address;
    }
    return prediction.display_name;
  }

  getPlaceDetails(prediction, currentLocationBoundsDistance) {
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => ({
        address: '',
        origin: latlng,
        bounds: locationBounds(latlng, currentLocationBoundsDistance),
      }));
    }

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    return Promise.resolve({
      address: this.getPredictionAddress(prediction),
      origin: placeOrigin(prediction),
      bounds: placeBounds(prediction),
      components: normalizeNominatimComponents(prediction),
      __provider: 'nominatim',
    });
  }
}

export default GeocoderNominatim;
